/**
 * WhatsApp paired confirmation card. Used by:
 *   - Onboarding go-live step (default mode: subscribe-on-mount activates
 *     the webhook subscription; Done becomes enabled on success).
 *   - Integrations page (skipSubscribe=true: subscription is already active
 *     from the original pairing — skip the call. `onReconnect` callback
 *     lets the page swap us out for the connect card without navigating).
 *
 * Sub-states (when running subscribe-on-mount):
 *   - subscribe_pending: /onboarding/whatsapp/subscribe in flight
 *   - subscribe_ok: webhook activated, Done can enable on page
 *   - subscribe_failed: retry button
 *
 * Shows the connected phone number + a "Wrong number? Reconnect" affordance.
 *
 * Phase 5 (2026-05-30): added `skipSubscribe` + `onReconnect` + `showFireCounts`
 * for integrations reuse.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, apiRequest } from '@/lib/api';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';

interface Props {
  /** Display phone number from /go-live-status (E.164 with formatting). */
  phoneNumber?: string | null;
  /** Called when subscribe succeeds, so the page can enable its Done button. */
  onSubscribeOk?: () => void;
  /** Integrations mode: skip the subscribe-on-mount call. The subscription
   *  is already active from the original pairing. */
  skipSubscribe?: boolean;
  /** When provided, Reconnect renders as a button calling this handler
   *  (integrations swaps to connect-card in place). Default = link to
   *  /onboarding/go-live. */
  onReconnect?: () => void;
  /** Integrations chrome: today + last-30 contacts received strip. */
  showFireCounts?: boolean;
  contactsToday?: number;
  contactsLast30d?: number;
}

export function WhatsAppConnectedCard({
  phoneNumber,
  onSubscribeOk,
  skipSubscribe = false,
  onReconnect,
  showFireCounts = false,
  contactsToday,
  contactsLast30d,
}: Props) {
  const queryClient = useQueryClient();
  const subscribedRef = useRef(false);
  const [state, setState] = useState<'pending' | 'ok' | 'failed'>(
    skipSubscribe ? 'ok' : 'pending',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skipSubscribe) return;
    if (subscribedRef.current) return;
    subscribedRef.current = true;
    void runSubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runSubscribe() {
    try {
      await apiRequest('/onboarding/whatsapp/subscribe', { method: 'POST' });
      setState('ok');
      onSubscribeOk?.();
      await queryClient.invalidateQueries({ queryKey: ['onboarding', 'go-live-status'] });
    } catch (e: unknown) {
      setState('failed');
      setError(
        (e instanceof ApiError && e.detail) ||
          "Couldn't activate the WhatsApp connection. Try again.",
      );
    }
  }

  function handleRetry() {
    setState('pending');
    setError(null);
    subscribedRef.current = false;
    subscribedRef.current = true;
    void runSubscribe();
  }

  if (state === 'failed') {
    return (
      <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
        <h3 className="text-xl font-bold text-dark">Connection setup failed</h3>
        <p className="mt-2 text-base text-muted-foreground leading-relaxed">
          We couldn't finish hooking up your WhatsApp. Try again, or contact us if it keeps failing.
        </p>
        {error && (
          <div className="mt-3">
            <Banner variant="error" size="compact">
              {error}
            </Banner>
          </div>
        )}
        <Button type="button" className="mt-4" onClick={handleRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (state === 'pending') {
    return (
      <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
        <h3 className="text-xl font-bold text-dark">Activating connection…</h3>
        <p className="mt-2 text-base text-muted-foreground leading-relaxed">
          Hooking HeyMotek up to your WhatsApp account. Usually under 30 seconds.
        </p>
      </div>
    );
  }

  // state === 'ok' — show paired confirmation
  return (
    <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-green-100">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-7 w-7 text-green-700">
            <path
              fillRule="evenodd"
              d="M16.704 5.293a1 1 0 0 1 .003 1.414l-7.5 7.55a1 1 0 0 1-1.42.003L3.29 9.797a1 1 0 1 1 1.42-1.41l3.79 3.82 6.79-6.835a1 1 0 0 1 1.414-.079Z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-dark">Your WhatsApp is paired</h3>
          <p className="mt-2 text-base text-muted-foreground leading-relaxed">
            Contacts from your ads will start being attributed automatically. Nothing changes about how you message.
          </p>

          <div className="mt-5 rounded-xl bg-bg-tint p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Connected number
            </p>
            <p className="mt-1 text-lg font-mono font-semibold text-dark">
              {phoneNumber || '—'}
            </p>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            Wrong number?
            {onReconnect ? (
              <button
                type="button"
                onClick={onReconnect}
                className="text-brand underline underline-offset-2 hover:no-underline ml-1"
              >
                Reconnect
              </button>
            ) : (
              <a
                href="/onboarding/go-live"
                className="text-brand underline underline-offset-2 hover:no-underline ml-1"
              >
                Reconnect
              </a>
            )}
          </p>

          {showFireCounts && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>
                <span className="font-bold text-dark">
                  {contactsToday ?? 0}
                </span>{' '}
                <span className="text-muted-foreground">contacts today</span>
              </span>
              <span className="text-dark/20">·</span>
              <span>
                <span className="font-bold text-dark">
                  {(contactsLast30d ?? 0).toLocaleString()}
                </span>{' '}
                <span className="text-muted-foreground">last 30 days</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
